/**
 * Payment Service
 * Handles interactions with the payment API
 */
import { logger } from '../utils/logger';

// Get the payment API endpoints from environment variables
const PROCESS_PAYMENT_ENDPOINT = process.env.REACT_APP_PROCESS_PAYMENT_ENDPOINT;
const UNIFIED_PAYMENTS_ENDPOINT = process.env.REACT_APP_PAYMENTS_ENDPOINT;

/**
 * Process a payment using Stripe
 * 
 * @param {Object} paymentData - Payment data
 * @param {string} paymentData.paymentMethodId - Stripe payment method ID
 * @param {number} paymentData.amount - Payment amount in dollars
 * @param {string} paymentData.email - Customer email
 * @param {string} paymentData.plan - Selected plan (basic or family)
 * @param {string} paymentData.firstName - Customer first name
 * @param {string} paymentData.lastName - Customer last name
 * @returns {Promise<Object>} - Payment result
 */
const processPayment = async (paymentData) => {
  try {
    // Check if unified payments endpoint is available
    if (UNIFIED_PAYMENTS_ENDPOINT) {
      logger.debug('Using unified payments endpoint');
      
      // Add paymentType for the unified endpoint
      const unifiedPaymentData = {
        ...paymentData,
        paymentType: 'regular'
      };
      
      const response = await fetch(UNIFIED_PAYMENTS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(unifiedPaymentData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Payment processing failed');
      }

      return data;
    } 
    // Fall back to the original endpoint if unified endpoint is not available
    else if (PROCESS_PAYMENT_ENDPOINT) {
      logger.debug('Using legacy process payment endpoint');
      
      const response = await fetch(PROCESS_PAYMENT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Payment processing failed');
      }

      return data;
    } else {
      throw new Error('Payment API endpoint is not configured');
    }
  } catch (error) {
    logger.error('Payment error:', error);
    throw error;
  }
};

export const paymentService = {
  processPayment,
};
